import type {
  AntiDetectionSignalKey,
  AntiDetectionSource,
  AntiDetectionTelemetryRow,
  SignalOutcome,
} from './catalog';
import { ANTI_DETECTION_SOURCES, SIGNAL_OUTCOMES } from './catalog';

/**
 * Pure aggregation helpers — kept out of the React component so unit
 * tests can pin the shape without rendering. Mirrors the server-side
 * row aggregation but applies it client-side for sparkline rendering.
 */

export interface SignalSummary {
  signalKey: AntiDetectionSignalKey | string;
  counts: Record<SignalOutcome, number>;
  totalCount: number;
  /** ISO of latest row across all outcomes (empty string when none). */
  lastFiredAt: string;
  /** Source mix counts (audit_log + connectivity_event + ...). */
  sourceCounts: Record<AntiDetectionSource, number>;
  /** 24-bucket sparkline values across all outcomes. */
  sparklineValues: number[];
}

/**
 * Build a per-signal summary keyed by `signalKey`. Output ordering is
 * irrelevant — the consumer joins on `signalKey` against the catalog
 * order. Signals with zero rows are emitted with all-zero counts so
 * the 16-tile grid always shows 16 tiles.
 */
export function summariseRowsBySignal(
  rows: AntiDetectionTelemetryRow[],
  bucketCount: number,
  windowFromMs: number,
  bucketWidthMs: number
): Map<string, SignalSummary> {
  const map = new Map<string, SignalSummary>();
  for (const row of rows) {
    const key = String(row.signalKey);
    let summary = map.get(key);
    if (!summary) {
      summary = {
        signalKey: row.signalKey,
        counts: { fire: 0, refuse: 0, degrade: 0 },
        totalCount: 0,
        lastFiredAt: '',
        sourceCounts: emptySourceCounts(),
        sparklineValues: new Array<number>(bucketCount).fill(0),
      };
      map.set(key, summary);
    }
    const outcome = SIGNAL_OUTCOMES.includes(row.outcome)
      ? row.outcome
      : 'fire';
    summary.counts[outcome] += row.count;
    summary.totalCount += row.count;
    if (ANTI_DETECTION_SOURCES.includes(row.source)) {
      summary.sourceCounts[row.source] += row.count;
    }
    if (!summary.lastFiredAt || row.lastSeenAt > summary.lastFiredAt) {
      summary.lastFiredAt = row.lastSeenAt;
    }
    const bucketIdx = bucketIndex(
      row.hourBucket,
      windowFromMs,
      bucketWidthMs,
      bucketCount
    );
    if (bucketIdx >= 0) {
      summary.sparklineValues[bucketIdx] += row.count;
    }
  }
  return map;
}

/**
 * Convert one telemetry row stream into `series[]` shape consumed by
 * `<StackedAreaChart>`. Filters to a single signal + outcome and
 * splits per source.
 */
export function buildStackedSeriesForOutcome(
  rows: AntiDetectionTelemetryRow[],
  signalKey: AntiDetectionSignalKey | string,
  outcome: SignalOutcome,
  bucketCount: number,
  windowFromMs: number,
  bucketWidthMs: number
): {
  series: { source: AntiDetectionSource; values: number[] }[];
  bucketLabels: string[];
} {
  const perSource = new Map<AntiDetectionSource, number[]>();
  for (const source of ANTI_DETECTION_SOURCES) {
    perSource.set(source, new Array<number>(bucketCount).fill(0));
  }
  for (const row of rows) {
    if (String(row.signalKey) !== String(signalKey)) continue;
    if (row.outcome !== outcome) continue;
    if (!ANTI_DETECTION_SOURCES.includes(row.source)) continue;
    const bucketIdx = bucketIndex(
      row.hourBucket,
      windowFromMs,
      bucketWidthMs,
      bucketCount
    );
    if (bucketIdx < 0) continue;
    const series = perSource.get(row.source);
    if (series) series[bucketIdx] += row.count;
  }
  const series = ANTI_DETECTION_SOURCES.map((source) => ({
    source,
    values: perSource.get(source) ?? [],
  }));
  const bucketLabels = buildBucketLabels(
    windowFromMs,
    bucketWidthMs,
    bucketCount
  );
  return { series, bucketLabels };
}

/**
 * Map an ISO hour-bucket string into a bucket index inside `[0,
 * bucketCount)` for the active window. Returns -1 for out-of-range.
 */
export function bucketIndex(
  hourBucketIso: string,
  windowFromMs: number,
  bucketWidthMs: number,
  bucketCount: number
): number {
  const t = Date.parse(hourBucketIso);
  if (!Number.isFinite(t)) return -1;
  const delta = t - windowFromMs;
  if (delta < 0) return -1;
  const idx = Math.floor(delta / bucketWidthMs);
  if (idx >= bucketCount) return -1;
  return idx;
}

/**
 * Pre-compute ISO labels for each bucket — exposed so the chart
 * x-axis can show real timestamps instead of synthetic indices.
 */
export function buildBucketLabels(
  windowFromMs: number,
  bucketWidthMs: number,
  bucketCount: number
): string[] {
  const labels: string[] = [];
  for (let i = 0; i < bucketCount; i++) {
    labels.push(new Date(windowFromMs + i * bucketWidthMs).toISOString());
  }
  return labels;
}

export function emptySourceCounts(): Record<AntiDetectionSource, number> {
  return {
    audit_log: 0,
    connectivity_event: 0,
    worker_log: 0,
    fire_marker_event: 0,
  };
}
